import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme, Grid2 } from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import {
  parseQueryParamsForOpsAnalyticsFilters,
  OpsAnalyticsFilters,
  OpsAnalyticsSelectedFilters,
} from "../../../../../models/Dashboard/OpsDashboard/OpsAnalyticsFilters";

import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import { Admin } from "../../../../../models/TaskTracker/Admin";
import AppSearchDropDown2 from "../../../../../views/components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../../../models/SortType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  parseQueryParamsForStockCheckAnalyticsFilters,
  StockCheckAnalyticsFilters,
  StockCheckAnalyticsSelectedFilters,
} from "../../../../../models/Dashboard/StockCheckDashboard/StockCheckAnalyticsFilters";
import TypeFilterCard from "../../components/filters/TypeFilterCard";

const STOCK_CHECK_ANALYTICS_FILTERS_KEY = "STOCK_CHECK_ANALYTICS_FILTERS_KEY";

const StockCheckAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    stockCheckAnalyticsDashboardStore,
    projectStore,
    orderStore,
    TaskTrackerStore,
    userStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [reloadList, setReloadList] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: StockCheckAnalyticsFilters =
    parseQueryParamsForStockCheckAnalyticsFilters(
      //   localStorage.getItem(STOCK_CHECK_ANALYTICS_FILTERS_KEY)
      //     ? JSON.parse(localStorage.getItem(STOCK_CHECK_ANALYTICS_FILTERS_KEY)!)
      //     : null,
      null,
      searchParams,
    );

  const [filters, setFilters] =
    useState<StockCheckAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<StockCheckAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      STOCK_CHECK_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters),
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.raised_by) {
      filters.raised_by.forEach((raised_by) =>
        newFilters.push({ id: "raised_by", value: raised_by.value }),
      );
    }

    if (filters.closed_by) {
      filters.closed_by.forEach((closed_by) =>
        newFilters.push({ id: "closed_by", value: closed_by.value }),
      );
    }

    setSelectedFiltersData(newFilters);
  };

  useEffect(() => {
    projectStore.getTypeOfOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        limit: 30,
        search: "",
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await stockCheckAnalyticsDashboardStore.getStockCheckAnalyticsTats({
        order_id: selectedFilters.order_id,
        raised_by: selectedFilters.raised_by,
        closed_by: selectedFilters.closed_by,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "raised_by":
        setFilters((prev) => ({
          ...prev,
          raised_by:
            filters.raised_by?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;

      case "closed_by":
        setFilters((prev) => ({
          ...prev,
          closed_by:
            prev.closed_by?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;

      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;

      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      raised_by: [],
      closed_by: [],
    }));

    setSelectedFiltersData([]);
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleAdminSearch = (searchText: string) => {
    if (searchText.length > 2) {
      TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
        showError("Error", err?.response?.data?.message);
      });
    }
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_STOCK_CHECK_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box
            sx={{
              margin: "8px",
              gap: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                gap: 2,
              }}
            >
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Date Range
                  </CustomTypography>
                  <DateRangePickerNew
                    onRangeSelect={handleRangeSelect}
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Raised By"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Raised By"
                    fontSize="0.9rem"
                    value={filters.raised_by || []}
                    list={
                      TaskTrackerStore?.admins?.map((admin: Admin) => ({
                        id: admin.id.toString(),
                        value: admin.name,
                      })) || []
                    }
                    local={false}
                    handleSearch={(raised_by) => handleAdminSearch(raised_by!)}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        raised_by: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Closed By"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Closed By"
                    fontSize="0.9rem"
                    value={filters.closed_by || []}
                    list={
                      TaskTrackerStore?.admins?.map((admin: Admin) => ({
                        id: admin.id.toString(),
                        value: admin.name,
                      })) || []
                    }
                    local={false}
                    handleSearch={(closed_by) => handleAdminSearch(closed_by!)}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        closed_by: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Order ID
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Order Id"
                    value={filters.order_id || []}
                    fontSize="12px"
                    list={orderStore.orderIds.map((item) => {
                      return { id: String(item), value: String(item) };
                    })}
                    local={false}
                    handleSearch={handleOrderIdSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_id: items,
                      }));
                    }}
                  />
                </Grid2>
              </Grid2>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                <Grid2
                  size={{ lg: 3, md: 4, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear Filters"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>

              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {selectedFiltersData.length > 0 && (
                  <Box sx={{ paddingTop: "15px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        borderRadius: 2,
                      }}
                    >
                      {selectedFiltersData.map((filter, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor:
                              theme.palette.customColors.lightPurple,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: theme.palette.customColors.blackBorder,
                            height: "34px",
                            paddingX: "6px",
                          }}
                        >
                          <CustomTypography
                            variant="heading9"
                            color={theme.palette.customColors.text}
                            style={{ marginRight: "5px" }}
                          >
                            {filter.value}
                          </CustomTypography>
                          <Button
                            onClick={() => handleRemoveFilter(index)}
                            sx={{
                              width: "14px",
                              height: "14px",
                              minWidth: "14px",
                              minHeight: "14px",
                              fontSize: "15px",
                              padding: "0px",
                              border: "1px solid #E2E2E2",
                              borderRadius: "50%",
                              backgroundColor: theme.palette.customColors.white,
                              cursor: "pointer",
                              overflow: "hidden",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid2>
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>

            <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
              {stockCheckAnalyticsDashboardStore.basic_tats?.map(
                (item, index) => (
                  <Grid2
                    size={{ lg: 2, md: 2, sm: 12, xs: 12 }}
                    key={`${item.label}-${index}`}
                  >
                    <TypeFilterCard
                      title={item.label}
                      message={item.info}
                      value={item.value ?? "0"}
                      color={""}
                      icon={
                        "https://content.swageazy.com/icons/boxes_green.png"
                      }
                      onClick={(type) => {
                        let url = "";
                        switch (type) {
                          case "Total Requests":
                            url = `/report/data?title=${encodeURI(
                              "Stock Check",
                            )}&base_url=/v5/ops-analytics/stock-check&path=/data/total-requests&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "Pending Requests":
                            url = `/report/data?title=${encodeURI(
                              "Stock Check - Pending Requests",
                            )}&base_url=/v5/ops-analytics/stock-check&path=/data/pending-requests&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "Cancelled Requests":
                            url = `/report/data?title=${encodeURI(
                              "Stock Check - Cancelled Requests",
                            )}&base_url=/v5/ops-analytics/stock-check&path=/data/cancelled-requests&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "Expired Requests":
                            url = `/report/data?title=${encodeURI(
                              "Stock Check - Expired Requests",
                            )}&base_url=/v5/ops-analytics/stock-check&path=/data/cancelled-requests&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "Closed Requests":
                          case "Closed Requests TAT (Hrs)":
                            url = `/report/data?title=${encodeURI(
                              "Stock Check - Closed Requests",
                            )}&base_url=/v5/ops-analytics/stock-check&path=/data/closed-requests&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "Projects For Approved Requests":
                          case "Approved to Projects Created TAT (Hrs)":
                            const newSearchParams = new URLSearchParams(
                              searchParams,
                            );
                            newSearchParams.set("project_created", "true");
                            url = `/report/data?title=${encodeURI(
                              "Stock Check - Closed Requests",
                            )}&base_url=/v5/ops-analytics/stock-check&path=/data/closed-requests&${newSearchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                        }
                      }}
                    />
                  </Grid2>
                ),
              )}
            </Grid2>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};
export default observer(StockCheckAnalyticsDashboard);
